import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import * as XLSX from 'xlsx';
import './AdminDashboard.css';

// Helper function to format payment method display
const formatPaymentMethod = (type, brand) => {
    const methodLabels = {
        'apple_pay': 'Apple Pay',
        'google_pay': 'Google Pay',
        'link': 'Link',
        'card': 'Card',
    };

    const brandLabels = {
        'visa': 'Visa',
        'mastercard': 'Mastercard',
        'amex': 'Amex',
        'discover': 'Discover',
        'diners': 'Diners',
        'jcb': 'JCB',
        'unionpay': 'UnionPay',
    };

    const methodLabel = methodLabels[type] || type || 'Card';
    const brandLabel = brandLabels[brand] || '';

    if (type === 'apple_pay' || type === 'google_pay' || type === 'link') {
        return methodLabel;
    }

    return brandLabel ? `${brandLabel}` : methodLabel;
};

// Helper function to format action type for display
const formatActionType = (type) => {
    const typeMap = {
        'login': { label: 'Login', color: '#28a745' },
        'login_failed': { label: 'Failed Login', color: '#dc3545' },
        'logout': { label: 'Logout', color: '#6c757d' },
        'session_expired': { label: 'Session Expired', color: '#ffc107' },
        'tile_verify': { label: 'Verify Tile', color: '#28a745' },
        'tile_reject': { label: 'Reject Tile', color: '#dc3545' },
        'tile_update': { label: 'Update Tile', color: '#17a2b8' },
        'tile_delete': { label: 'Delete Tile', color: '#dc3545' },
        'tile_add_reserved': { label: 'Add Reserved', color: '#007bff' },
        'tile_inquiry': { label: 'New Inquiry', color: '#17a2b8' },
        'inquiry_confirmed': { label: 'Inquiry Confirmed', color: '#28a745' },
        'inquiry_contacted': { label: 'Inquiry Contacted', color: '#17a2b8' },
        'inquiry_cancelled': { label: 'Inquiry Cancelled', color: '#dc3545' },
        'inquiry_delete': { label: 'Inquiry Deleted', color: '#dc3545' },
    };
    return typeMap[type] || { label: type || 'Action', color: '#6c757d' };
};

// Helper function to format payment plan for display
const formatPaymentPlan = (plan) => {
    const planMap = {
        'full': 'Full Payment',
        '3': '3 Monthly Instalments',
        '6': '6 Monthly Instalments',
        '12': '12 Monthly Instalments',
    };
    return planMap[plan] || plan;
};

// Helper function to format inquiry status
const formatInquiryStatus = (status) => {
    const statusMap = {
        'pending': { label: 'New Inquiry', color: '#ffc107', bgColor: '#fff3cd' },
        'contacted': { label: 'Contacted', color: '#17a2b8', bgColor: '#d1ecf1' },
        'confirmed': { label: 'Confirmed', color: '#28a745', bgColor: '#d4edda' },
        'cancelled': { label: 'Cancelled', color: '#dc3545', bgColor: '#f8d7da' },
    };
    return statusMap[status] || { label: status, color: '#6c757d', bgColor: '#e2e3e5' };
};

// Helper function to format date as dd/mm/yyyy
const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

// Helper function to format date and time as dd/mm/yyyy HH:MM:SS
const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const RESERVED_TILES_COUNT = 75; // First 3 rows (25 tiles per row)
const SESSION_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds

const AdminDashboard = () => {
    const { user, logout, isAuthenticated, isLoading: authLoading } = useAuth();
    const [activeTab, setActiveTab] = useState('tiles');
    const [services, setServices] = useState([]);
    const [events, setEvents] = useState([]);
    const [blogs, setBlogs] = useState([]);
    const [volunteers, setVolunteers] = useState([]);
    const [tileReservations, setTileReservations] = useState([]);
    const [inquiries, setInquiries] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);
    const [adminUsers, setAdminUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showReservedForm, setShowReservedForm] = useState(false);
    const [reservedTileNumber, setReservedTileNumber] = useState('');
    const [reservedFullName, setReservedFullName] = useState('');
    const [reservedDisplayName, setReservedDisplayName] = useState('');
    const [reservedDonorEmail, setReservedDonorEmail] = useState('');
    const [reservedDonorPhone, setReservedDonorPhone] = useState('');
    const [reservedFormError, setReservedFormError] = useState('');
    const [sessionTimeLeft, setSessionTimeLeft] = useState(SESSION_TIMEOUT);
    const [lastActivity, setLastActivity] = useState(Date.now());
    // Verification modal state
    const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [verifyingTile, setVerifyingTile] = useState(null);
    const [verifyFullName, setVerifyFullName] = useState('');
    const [verifyDisplayName, setVerifyDisplayName] = useState('');
    const [verifyEmail, setVerifyEmail] = useState('');
    const [verifyPhone, setVerifyPhone] = useState('');
    // Payment confirmation modal state
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentTile, setPaymentTile] = useState(null);
    const [stripePaymentId, setStripePaymentId] = useState('');
    const [paymentNotes, setPaymentNotes] = useState('');
    // Rejection/Delete modal state
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectingTile, setRejectingTile] = useState(null);
    const [rejectNotes, setRejectNotes] = useState('');
    const [rejectAction, setRejectAction] = useState('reject'); // 'reject' or 'delete'
    const navigate = useNavigate();

    // Session timeout handler
    const handleLogout = useCallback(async (isTimeout = false) => {
        // Use Auth0 logout
        logout({
            logoutParams: {
                returnTo: window.location.origin + '/admin' + (isTimeout ? '?timeout=true' : '')
            }
        });
    }, [logout]);

    // Auth0 authentication check and data loading
    useEffect(() => {
        if (authLoading) return;

        if (!isAuthenticated) {
            navigate('/admin');
            return;
        }

        fetchData();

        // Activity tracking - debounced for session timeout display
        let activityTimeout;
        const resetActivity = () => {
            clearTimeout(activityTimeout);
            activityTimeout = setTimeout(() => {
                setLastActivity(Date.now());
            }, 1000); // Debounce 1 second
        };

        // Track user activity
        window.addEventListener('mousemove', resetActivity);
        window.addEventListener('keypress', resetActivity);
        window.addEventListener('click', resetActivity);
        window.addEventListener('scroll', resetActivity);

        return () => {
            clearTimeout(activityTimeout);
            window.removeEventListener('mousemove', resetActivity);
            window.removeEventListener('keypress', resetActivity);
            window.removeEventListener('click', resetActivity);
            window.removeEventListener('scroll', resetActivity);
        };
    }, [navigate, isAuthenticated, authLoading]);

    // Session timeout countdown
    useEffect(() => {
        const interval = setInterval(() => {
            const elapsed = Date.now() - lastActivity;
            const remaining = SESSION_TIMEOUT - elapsed;

            if (remaining <= 0) {
                handleLogout(true);
            } else {
                setSessionTimeLeft(remaining);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [lastActivity, handleLogout]);

    const fetchData = async () => {
        try {
            const [tilesRes, auditRes, inquiriesRes, usersRes, servicesRes, eventsRes, blogsRes, volunteersRes] = await Promise.all([
                fetch('/api/wall-tiles/admin'),
                fetch('/api/audit?limit=100'),
                fetch('/api/inquiries'),
                fetch('/api/admin/users').catch(() => ({ ok: false })),
                fetch('/api/services?all=true'),
                fetch('/api/events?all=true'),
                fetch('/api/blogs?all=true'),
                fetch('/api/volunteers?all=true')
            ]);

            const tilesData = await tilesRes.json();
            const auditData = await auditRes.json();
            const inquiriesData = await inquiriesRes.json();
            const usersData = usersRes.ok ? await usersRes.json() : [];
            const servicesData = await servicesRes.json();
            const eventsData = await eventsRes.json();
            const blogsData = await blogsRes.json();
            const volunteersData = await volunteersRes.json();

            setTileReservations(Array.isArray(tilesData) ? tilesData : []);
            setAuditLogs(auditData?.logs || []);
            setInquiries(Array.isArray(inquiriesData) ? inquiriesData : []);
            setAdminUsers(Array.isArray(usersData) ? usersData : []);
            setServices(Array.isArray(servicesData) ? servicesData : []);
            setEvents(Array.isArray(eventsData) ? eventsData : []);
            setBlogs(Array.isArray(blogsData) ? blogsData : []);
            setVolunteers(Array.isArray(volunteersData) ? volunteersData : []);
        } catch (error) {
            console.error('Error fetching data:', error);
            setTileReservations([]);
            setAuditLogs([]);
            setInquiries([]);
            setAdminUsers([]);
            setServices([]);
            setEvents([]);
            setBlogs([]);
            setVolunteers([]);
        } finally {
            setLoading(false);
        }
    };

    const updateTileStatus = async (tileNumber, status, additionalData = {}) => {
        try {
            const response = await fetch('/api/wall-tiles/admin', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tile_number: tileNumber, status, ...additionalData })
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Failed to update tile status');
            }
            await fetchData();
            return true;
        } catch (error) {
            console.error('Error updating tile status:', error);
            alert('Error updating tile status: ' + error.message);
            return false;
        }
    };

    // Open verification modal with tile data
    const openVerifyModal = (tile) => {
        // Check if payment has been confirmed first (for all tiles except old admin tiles)
        if (tile.payment_status !== 'paid' && tile.payment_status !== 'admin') {
            alert('Payment must be confirmed before verifying the tile. Please confirm payment first by clicking on "Pending" in the Payment column.');
            return;
        }
        setVerifyingTile(tile);
        setVerifyFullName(tile.donor_full_name || tile.donor_name || '');
        setVerifyDisplayName(tile.donor_name || '');
        setVerifyEmail(tile.donor_email || '');
        setVerifyPhone(tile.donor_phone || '');
        setShowVerifyModal(true);
    };

    // Close verification modal
    const closeVerifyModal = () => {
        setShowVerifyModal(false);
        setVerifyingTile(null);
        setVerifyFullName('');
        setVerifyDisplayName('');
        setVerifyEmail('');
        setVerifyPhone('');
    };

    // Confirm and lock the tile
    const confirmVerification = async () => {
        if (!verifyDisplayName.trim()) {
            alert('Display name is required');
            return;
        }
        if (!verifyEmail.trim()) {
            alert('Email is required');
            return;
        }

        const success = await updateTileStatus(verifyingTile.tile_number, 'confirmed', {
            donor_name: verifyDisplayName.trim(),
            donor_full_name: verifyFullName.trim(),
            donor_email: verifyEmail.trim(),
            donor_phone: verifyPhone.trim()
        });

        if (success) {
            closeVerifyModal();
        }
    };

    // Open payment confirmation modal
    const openPaymentModal = (tile) => {
        setPaymentTile(tile);
        setStripePaymentId(tile.stripe_payment_id || '');
        setPaymentNotes('');
        setShowPaymentModal(true);
    };

    // Close payment confirmation modal
    const closePaymentModal = () => {
        setShowPaymentModal(false);
        setPaymentTile(null);
        setStripePaymentId('');
        setPaymentNotes('');
    };

    // Confirm payment with Stripe ID and notes
    const confirmPayment = async () => {
        if (!stripePaymentId.trim()) {
            alert('Please enter the Stripe Payment ID');
            return;
        }

        // Build comprehensive admin notes with payment details
        const now = new Date();
        const timestamp = now.toLocaleString('en-AU', { timeZone: 'Australia/Sydney' });
        const paymentMethod = paymentTile.payment_method_type
            ? formatPaymentMethod(paymentTile.payment_method_type, paymentTile.payment_method_brand)
            : 'Card';

        const autoNotes = `Payment confirmed on ${timestamp} | Stripe ID: ${stripePaymentId.trim()} | Method: ${paymentMethod}`;
        const fullNotes = paymentNotes.trim()
            ? `${autoNotes} | Notes: ${paymentNotes.trim()}`
            : autoNotes;

        try {
            const response = await fetch('/api/wall-tiles/admin/payment', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tile_number: paymentTile.tile_number,
                    payment_status: 'paid',
                    stripe_payment_id: stripePaymentId.trim(),
                    admin_notes: fullNotes
                })
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Failed to confirm payment');
            }
            await fetchData();
            closePaymentModal();
        } catch (error) {
            console.error('Error confirming payment:', error);
            alert('Error confirming payment: ' + error.message);
        }
    };

    const deleteTileReservation = async (tileNumber, notes) => {
        try {
            await fetch(`/api/wall-tiles/admin?tile_number=${tileNumber}&notes=${encodeURIComponent(notes || '')}`, { method: 'DELETE' });
            fetchData();
        } catch (error) {
            alert('Error deleting tile reservation');
        }
    };

    // Open rejection/delete modal
    const openRejectModal = (tile, action = 'reject') => {
        setRejectingTile(tile);
        setRejectAction(action);
        setRejectNotes('');
        setShowRejectModal(true);
    };

    // Close rejection modal
    const closeRejectModal = () => {
        setShowRejectModal(false);
        setRejectingTile(null);
        setRejectNotes('');
        setRejectAction('reject');
    };

    // Confirm rejection or deletion with notes
    const confirmRejectAction = async () => {
        const wordCount = rejectNotes.trim().split(/\s+/).filter(w => w.length > 0).length;
        if (wordCount < 5) {
            alert('Please provide at least 5 words explaining the reason.');
            return;
        }

        if (rejectAction === 'delete') {
            await deleteTileReservation(rejectingTile.tile_number, rejectNotes);
        } else {
            await updateTileStatus(rejectingTile.tile_number, 'rejected', { admin_notes: rejectNotes });
        }
        closeRejectModal();
    };

    // Export wall tiles data to XLSX
    const exportTilesToXLSX = () => {
        if (!tileReservations || tileReservations.length === 0) {
            alert('No tile data to export');
            return;
        }

        // Prepare data for export with formatted columns
        const exportData = tileReservations.map(tile => ({
            'Tile #': tile.tile_number,
            'Display Name': tile.donor_name || '',
            'Full Name': tile.donor_full_name || tile.donor_name || '',
            'Email': tile.donor_email || '',
            'Phone': tile.donor_phone || '',
            'Status': tile.status === 'confirmed' ? 'Verified' : tile.status === 'pending' ? 'Pending Verification' : tile.status,
            'Payment Status': tile.payment_status === 'paid' ? 'Paid' : tile.payment_status || 'Pending',
            'Payment Method': tile.payment_method_type ? formatPaymentMethod(tile.payment_method_type, tile.payment_method_brand) : '',
            'Stripe Session ID': tile.stripe_session_id || '',
            'Stripe Payment ID': tile.stripe_payment_id || '',
            'Created At': tile.created_at ? new Date(tile.created_at).toLocaleString() : '',
            'Updated At': tile.updated_at ? new Date(tile.updated_at).toLocaleString() : ''
        }));

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(exportData);

        // Set column widths for better readability
        ws['!cols'] = [
            { wch: 8 },   // Tile #
            { wch: 20 },  // Display Name
            { wch: 25 },  // Full Name
            { wch: 30 },  // Email
            { wch: 15 },  // Phone
            { wch: 18 },  // Status
            { wch: 15 },  // Payment Status
            { wch: 15 },  // Payment Method
            { wch: 40 },  // Stripe Session ID
            { wch: 30 },  // Stripe Payment ID
            { wch: 20 },  // Created At
            { wch: 20 }   // Updated At
        ];

        XLSX.utils.book_append_sheet(wb, ws, 'Wall Tiles');

        // Generate filename with current date
        const dateStr = new Date().toISOString().split('T')[0];
        const filename = `PCIC_Wall_Tiles_${dateStr}.xlsx`;

        // Download the file
        XLSX.writeFile(wb, filename);
    };

    const handleAddReservedTile = async (e) => {
        e.preventDefault();
        setReservedFormError('');

        const tileNum = parseInt(reservedTileNumber);
        if (isNaN(tileNum) || tileNum < 1 || tileNum > RESERVED_TILES_COUNT) {
            setReservedFormError(`Tile number must be between 1 and ${RESERVED_TILES_COUNT}`);
            return;
        }

        // Check if tile is already taken
        const existingTile = tileReservations.find(t => t.tile_number === tileNum);
        if (existingTile) {
            setReservedFormError(`Tile #${tileNum} is already assigned to ${existingTile.donor_name}`);
            return;
        }

        try {
            const response = await fetch('/api/wall-tiles/admin/reserved', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tile_number: tileNum,
                    donor_full_name: reservedFullName.trim(),
                    donor_name: reservedDisplayName.trim(),
                    donor_email: reservedDonorEmail.trim(),
                    donor_phone: reservedDonorPhone.trim()
                })
            });

            if (response.ok) {
                setShowReservedForm(false);
                setReservedTileNumber('');
                setReservedFullName('');
                setReservedDisplayName('');
                setReservedDonorEmail('');
                setReservedDonorPhone('');
                fetchData();
            } else {
                const data = await response.json();
                setReservedFormError(data.error || 'Failed to add reserved tile');
            }
        } catch (error) {
            setReservedFormError('Error adding reserved tile');
        }
    };

    const updateInquiryStatus = async (inquiryId, status, notes = null) => {
        try {
            const body = { inquiry_id: inquiryId, status };
            if (notes !== null) body.admin_notes = notes;

            await fetch('/api/inquiries', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            fetchData();
        } catch (error) {
            alert('Error updating inquiry status');
        }
    };

    const deleteInquiry = async (inquiryId) => {
        if (!window.confirm('Are you sure you want to delete this inquiry? This will also release the reserved tiles.')) return;

        try {
            await fetch(`/api/inquiries?id=${inquiryId}`, { method: 'DELETE' });
            fetchData();
        } catch (error) {
            alert('Error deleting inquiry');
        }
    };

    const updateUserApproval = async (userId, action) => {
        const actionText = action === 'approve' ? 'approve' : 'reject';
        if (!window.confirm(`Are you sure you want to ${actionText} this user?`)) return;

        try {
            await fetch('/api/admin/users', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: userId,
                    action: action,
                    approved_by: user?.email || 'admin'
                })
            });
            fetchData();
        } catch (error) {
            alert(`Error ${actionText}ing user`);
        }
    };

    const transferPrimaryAdmin = async (userId, targetEmail) => {
        if (!window.confirm(`Are you sure you want to transfer Primary Admin status to ${targetEmail}? You will no longer be the primary admin.`)) return;

        try {
            const response = await fetch('/api/admin/users', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: userId,
                    action: 'transfer_primary',
                    requester_email: user?.email
                })
            });
            const data = await response.json();
            if (response.ok) {
                alert(data.message || 'Primary admin transferred successfully');
                fetchData();
            } else {
                alert(data.error || 'Failed to transfer primary admin');
            }
        } catch (error) {
            alert('Error transferring primary admin');
        }
    };

    const deleteUser = async (userId, targetEmail) => {
        if (!window.confirm(`Are you sure you want to permanently delete ${targetEmail}? This action cannot be undone.`)) return;

        try {
            const response = await fetch(`/api/admin/users?user_id=${userId}&requester_email=${encodeURIComponent(user?.email)}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            if (response.ok) {
                alert(data.message || 'User deleted successfully');
                fetchData();
            } else {
                alert(data.error || 'Failed to delete user');
            }
        } catch (error) {
            alert('Error deleting user');
        }
    };

    // Check if current user is primary admin
    const currentUserIsPrimary = adminUsers.find(u => u.email === user?.email)?.is_primary_admin;

    const formatTimeLeft = (ms) => {
        const minutes = Math.floor(ms / 60000);
        const seconds = Math.floor((ms % 60000) / 1000);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    if (loading) {
        return <div className="admin-loading">Loading...</div>;
    }

    return (
        <div className="admin-dashboard">
            <header className="admin-header">
                <h1>PCIC Admin Dashboard</h1>
                <div className="header-right">
                    {user && (
                        <span className="user-info" title={user.email}>
                            {user.name || user.email}
                        </span>
                    )}
                    <span className={`session-timer ${sessionTimeLeft < 60000 ? 'warning' : ''}`} title="Session expires in">
                        {formatTimeLeft(sessionTimeLeft)}
                    </span>
                    <button onClick={() => handleLogout(false)} className="logout-btn">Logout</button>
                </div>
            </header>

            <nav className="admin-nav">
                <button
                    className={activeTab === 'services' ? 'active' : ''}
                    onClick={() => setActiveTab('services')}
                >
                    Services
                </button>
                <button
                    className={activeTab === 'events' ? 'active' : ''}
                    onClick={() => setActiveTab('events')}
                >
                    Events
                </button>
                <button
                    className={activeTab === 'blogs' ? 'active' : ''}
                    onClick={() => setActiveTab('blogs')}
                >
                    Blogs
                </button>
                <button
                    className={activeTab === 'volunteers' ? 'active' : ''}
                    onClick={() => setActiveTab('volunteers')}
                >
                    Volunteers
                </button>
                <button
                    className={activeTab === 'inquiries' ? 'active' : ''}
                    onClick={() => setActiveTab('inquiries')}
                >
                    Inquiries ({inquiries.filter(i => i.status === 'pending').length} new)
                </button>
                <button
                    className={activeTab === 'tiles' ? 'active' : ''}
                    onClick={() => setActiveTab('tiles')}
                >
                    Wall Tiles ({tileReservations.filter(t => t.status === 'pending').length} pending)
                </button>
                <button
                    className={activeTab === 'audit' ? 'active' : ''}
                    onClick={() => setActiveTab('audit')}
                >
                    Audit Trail
                </button>
                <button
                    className={activeTab === 'users' ? 'active' : ''}
                    onClick={() => setActiveTab('users')}
                >
                    Users ({adminUsers.filter(u => u.approval_status === 'pending').length} pending)
                </button>
            </nav>

            <main className="admin-content">
                {activeTab === 'services' && (
                    <div className="admin-section">
                        <div className="section-header">
                            <h2>Services</h2>
                            <button className="add-btn" onClick={() => navigate('/admin/services/new')}>+ Add Service</button>
                        </div>
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Title</th>
                                    <th>Subtitle</th>
                                    <th>Icon</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {services.map(service => (
                                    <tr key={service.id}>
                                        <td><strong>{service.title}</strong></td>
                                        <td>{service.subtitle || '-'}</td>
                                        <td><i className={service.icon}></i> {service.icon || '-'}</td>
                                        <td>
                                            <span className={`status-badge status-${service.is_active ? 'approved' : 'pending'}`}>
                                                {service.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td>
                                            <button onClick={() => navigate(`/admin/services/${service.id}`)} className="edit-btn">Edit</button>
                                        </td>
                                    </tr>
                                ))}
                                {services.length === 0 && (
                                    <tr><td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>No services yet. Click "+ Add Service" to create one.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'events' && (
                    <div className="admin-section">
                        <div className="section-header">
                            <h2>Events</h2>
                            <button className="add-btn" onClick={() => navigate('/admin/events/new')}>+ Add Event</button>
                        </div>
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Title</th>
                                    <th>Date</th>
                                    <th>Time</th>
                                    <th>Location</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map(event => (
                                    <tr key={event.id}>
                                        <td><strong>{event.title}</strong></td>
                                        <td>{event.event_date || '-'}</td>
                                        <td>{event.event_time || '-'}</td>
                                        <td>{event.location || '-'}</td>
                                        <td>
                                            <span className={`status-badge status-${event.is_active ? 'approved' : 'pending'}`}>
                                                {event.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td>
                                            <button onClick={() => navigate(`/admin/events/${event.id}`)} className="edit-btn">Edit</button>
                                        </td>
                                    </tr>
                                ))}
                                {events.length === 0 && (
                                    <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>No events yet. Click "+ Add Event" to create one.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'blogs' && (
                    <div className="admin-section">
                        <div className="section-header">
                            <h2>Blogs & News</h2>
                            <button className="add-btn" onClick={() => navigate('/admin/blogs/new')}>+ Add Blog Post</button>
                        </div>
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Title</th>
                                    <th>Author</th>
                                    <th>Tags</th>
                                    <th>Published</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {blogs.map(blog => {
                                    let tags = [];
                                    try { tags = JSON.parse(blog.tags || '[]'); } catch (e) {}
                                    return (
                                        <tr key={blog.id}>
                                            <td><strong>{blog.title}</strong></td>
                                            <td>{blog.author || '-'}</td>
                                            <td>
                                                {tags.length > 0 ? tags.map((tag, i) => (
                                                    <span key={i} style={{
                                                        background: '#e9ecef',
                                                        padding: '2px 8px',
                                                        borderRadius: '12px',
                                                        marginRight: '4px',
                                                        fontSize: '12px'
                                                    }}>{tag}</span>
                                                )) : '-'}
                                            </td>
                                            <td>{blog.published_at ? formatDate(blog.published_at) : '-'}</td>
                                            <td>
                                                <span className={`status-badge status-${blog.is_published ? 'approved' : 'pending'}`}>
                                                    {blog.is_published ? 'Published' : 'Draft'}
                                                </span>
                                            </td>
                                            <td>
                                                <button onClick={() => navigate(`/admin/blogs/${blog.id}`)} className="edit-btn">Edit</button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {blogs.length === 0 && (
                                    <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>No blog posts yet. Click "+ Add Blog Post" to create one.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'volunteers' && (
                    <div className="admin-section">
                        <div className="section-header">
                            <h2>Volunteers / Committee Members</h2>
                            <button className="add-btn" onClick={() => navigate('/admin/volunteers/new')}>+ Add Member</button>
                        </div>
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {volunteers.map(volunteer => (
                                    <tr key={volunteer.id} className={volunteer.status === 'pending' ? 'pending-row' : ''}>
                                        <td><strong>{volunteer.name}</strong></td>
                                        <td>{volunteer.email}</td>
                                        <td>{volunteer.phone || '-'}</td>
                                        <td>{volunteer.role || 'Volunteer'}</td>
                                        <td>
                                            <span className={`status-badge status-${volunteer.status === 'approved' ? 'approved' : volunteer.status === 'rejected' ? 'rejected' : 'pending'}`}>
                                                {volunteer.status === 'approved' ? 'Approved' : volunteer.status === 'rejected' ? 'Rejected' : 'Pending'}
                                            </span>
                                        </td>
                                        <td>
                                            <button onClick={() => navigate(`/admin/volunteers/${volunteer.id}`)} className="edit-btn">Edit</button>
                                        </td>
                                    </tr>
                                ))}
                                {volunteers.length === 0 && (
                                    <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>No volunteers or committee members yet. Click "+ Add Member" to add one.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'inquiries' && (
                    <div className="admin-section">
                        <div className="section-header">
                            <h2>Tile Inquiries</h2>
                            <div className="tile-stats">
                                <span className="tile-stat">
                                    <strong>{inquiries.filter(i => i.status === 'pending').length}</strong> New
                                </span>
                                <span className="tile-stat">
                                    <strong>{inquiries.filter(i => i.status === 'contacted').length}</strong> Contacted
                                </span>
                                <span className="tile-stat">
                                    <strong>{inquiries.filter(i => i.status === 'confirmed').length}</strong> Confirmed
                                </span>
                            </div>
                        </div>

                        <div className="inquiries-info-banner">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <path d="M12 16v-4M12 8h.01"/>
                            </svg>
                            <p>These are tile inquiries from users interested in supporting the mosque. Please contact them to arrange payment and confirm their tiles.</p>
                        </div>

                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Tiles</th>
                                    <th>Donor Details</th>
                                    <th>Payment Plan</th>
                                    <th>Total</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {inquiries.map(inquiry => {
                                    const statusInfo = formatInquiryStatus(inquiry.status);
                                    const tileNums = JSON.parse(inquiry.tile_numbers || '[]');
                                    return (
                                        <tr key={inquiry.inquiry_id} className={inquiry.status === 'pending' ? 'pending-row' : ''}>
                                            <td className="audit-time">
                                                {formatDate(inquiry.created_at)}<br/>
                                                <small>{new Date(inquiry.created_at).toLocaleTimeString()}</small>
                                            </td>
                                            <td>
                                                <div className="inquiry-tiles">
                                                    {tileNums.map(num => (
                                                        <span key={num} className="inquiry-tile-badge">#{num}</span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td>
                                                <strong>{inquiry.donor_name}</strong><br/>
                                                <small>{inquiry.donor_email}</small><br/>
                                                <small>{inquiry.donor_phone}</small>
                                                {inquiry.donor_display_name && inquiry.donor_display_name !== inquiry.donor_name && (
                                                    <><br/><small className="display-name">Display: {inquiry.donor_display_name}</small></>
                                                )}
                                            </td>
                                            <td>
                                                <span className="payment-plan-badge">
                                                    {formatPaymentPlan(inquiry.payment_plan)}
                                                </span>
                                                {inquiry.instalment_amount && (
                                                    <><br/><small>${inquiry.instalment_amount.toLocaleString()}/month</small></>
                                                )}
                                            </td>
                                            <td>
                                                <strong>${inquiry.total_amount?.toLocaleString() || '0'} AUD</strong>
                                            </td>
                                            <td>
                                                <span
                                                    className="inquiry-status-badge"
                                                    style={{ backgroundColor: statusInfo.bgColor, color: statusInfo.color }}
                                                >
                                                    {statusInfo.label}
                                                </span>
                                                {inquiry.followed_up_at && (
                                                    <><br/><small>Followed up: {formatDate(inquiry.followed_up_at)}</small></>
                                                )}
                                            </td>
                                            <td>
                                                <div className="inquiry-actions">
                                                    {inquiry.status === 'pending' && (
                                                        <button
                                                            onClick={() => updateInquiryStatus(inquiry.inquiry_id, 'contacted')}
                                                            className="edit-btn"
                                                            title="Mark as contacted"
                                                        >
                                                            Mark Contacted
                                                        </button>
                                                    )}
                                                    {(inquiry.status === 'pending' || inquiry.status === 'contacted') && (
                                                        <button
                                                            onClick={() => updateInquiryStatus(inquiry.inquiry_id, 'confirmed')}
                                                            className="approve-btn"
                                                            title="Confirm tiles for this donor"
                                                        >
                                                            Confirm
                                                        </button>
                                                    )}
                                                    {inquiry.status !== 'cancelled' && inquiry.status !== 'confirmed' && (
                                                        <button
                                                            onClick={() => updateInquiryStatus(inquiry.inquiry_id, 'cancelled')}
                                                            className="reject-btn"
                                                            title="Cancel this inquiry"
                                                        >
                                                            Cancel
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => deleteInquiry(inquiry.inquiry_id)}
                                                        className="delete-btn"
                                                        title="Delete this inquiry"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {inquiries.length === 0 && (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                            No inquiries yet. When users submit tile inquiries from the Wall of Akhirah page, they will appear here.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'tiles' && (
                    <div className="admin-section">
                        <div className="section-header">
                            <div className="section-title-row">
                                <h2>Wall of Akhirah - Tile Reservations</h2>
                                <button
                                    className="export-btn"
                                    onClick={exportTilesToXLSX}
                                    title="Download all tile data as Excel file"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                        <polyline points="7 10 12 15 17 10"/>
                                        <line x1="12" y1="15" x2="12" y2="3"/>
                                    </svg>
                                    Export XLSX
                                </button>
                            </div>
                            <div className="tile-stats">
                                <span className="tile-stat">
                                    <strong>{tileReservations.filter(t => t.status === 'pending').length}</strong> Pending Verification
                                </span>
                                <span className="tile-stat">
                                    <strong>{tileReservations.filter(t => t.status === 'confirmed').length}</strong> Verified
                                </span>
                                <span className="tile-stat">
                                    <strong>{500 - tileReservations.filter(t => ['pending', 'confirmed'].includes(t.status)).length}</strong> Available
                                </span>
                            </div>
                        </div>

                        {/* Reserved Tiles Section (First 3 rows) */}
                        <div className="reserved-tiles-section">
                            <div className="reserved-tiles-header">
                                <h3>Reserved Tiles (Rows 1-3: Tiles #1-75)</h3>
                                <p>These tiles are reserved for admin allocation only and cannot be purchased by the public.</p>
                                <button
                                    className="add-btn"
                                    onClick={() => setShowReservedForm(!showReservedForm)}
                                >
                                    {showReservedForm ? 'Cancel' : '+ Add Reserved Tile'}
                                </button>
                            </div>

                            {showReservedForm && (
                                <form className="reserved-tile-form" onSubmit={handleAddReservedTile}>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Tile Number (1-75) *</label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="75"
                                                value={reservedTileNumber}
                                                onChange={(e) => setReservedTileNumber(e.target.value)}
                                                placeholder="Enter tile number"
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Full Name *</label>
                                            <input
                                                type="text"
                                                value={reservedFullName}
                                                onChange={(e) => setReservedFullName(e.target.value)}
                                                placeholder="Enter full name"
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Display Name (for tile) *</label>
                                            <input
                                                type="text"
                                                value={reservedDisplayName}
                                                onChange={(e) => setReservedDisplayName(e.target.value)}
                                                placeholder="Name to show on tile"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Email *</label>
                                            <input
                                                type="email"
                                                value={reservedDonorEmail}
                                                onChange={(e) => setReservedDonorEmail(e.target.value)}
                                                placeholder="Enter email"
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Phone (optional)</label>
                                            <input
                                                type="tel"
                                                value={reservedDonorPhone}
                                                onChange={(e) => setReservedDonorPhone(e.target.value)}
                                                placeholder="Enter phone"
                                            />
                                        </div>
                                    </div>
                                    <p style={{ fontSize: '12px', color: '#666', marginTop: '8px', marginBottom: '12px' }}>
                                        Reserved tiles will start with Pending payment status and require the same verification flow as regular tiles.
                                    </p>
                                    {reservedFormError && <p className="form-error">{reservedFormError}</p>}
                                    <button type="submit" className="approve-btn">Add Reserved Tile</button>
                                </form>
                            )}
                        </div>

                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Tile #</th>
                                    <th>Full Name</th>
                                    <th>Display Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Status</th>
                                    <th>Payment</th>
                                    <th>Payment Option</th>
                                    <th>Notes</th>
                                    <th>Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tileReservations.map(tile => (
                                    <tr key={tile.tile_number} className={tile.status === 'pending' ? 'pending-row' : ''}>
                                        <td>
                                            <strong>#{tile.tile_number}</strong>
                                            {tile.tile_number <= RESERVED_TILES_COUNT && (
                                                <span className="reserved-badge">Reserved</span>
                                            )}
                                        </td>
                                        <td>{tile.donor_full_name || tile.donor_name}</td>
                                        <td>{tile.donor_name}</td>
                                        <td>{tile.donor_email}</td>
                                        <td>{tile.donor_phone || '-'}</td>
                                        <td>
                                            <span className={`status-badge status-${tile.status === 'confirmed' ? 'approved' : tile.status}`}>
                                                {tile.status === 'pending' ? 'Pending Verification' : tile.status === 'confirmed' ? 'Verified' : tile.status}
                                            </span>
                                        </td>
                                        <td>
                                            {tile.payment_status === 'paid' ? (
                                                <span className="payment-status paid">Paid</span>
                                            ) : tile.payment_status === 'admin' ? (
                                                <span className="payment-status admin">N/A</span>
                                            ) : (
                                                <button
                                                    className="payment-status pending clickable"
                                                    onClick={() => openPaymentModal(tile)}
                                                    title="Click to confirm payment"
                                                >
                                                    Pending
                                                </button>
                                            )}
                                        </td>
                                        <td>
                                            {tile.payment_status === 'admin' ? (
                                                <span className="payment-option admin">Reserved (Admin)</span>
                                            ) : tile.tile_number <= RESERVED_TILES_COUNT ? (
                                                <span className="payment-option reserved">Reserved</span>
                                            ) : tile.payment_status?.startsWith('instalment_') ? (
                                                <div className="payment-option-info">
                                                    <span className="payment-option instalment">
                                                        {tile.payment_status.replace('instalment_', '')} months
                                                    </span>
                                                    <span className="payment-dates">
                                                        Start: {formatDate(tile.created_at)}<br/>
                                                        End: {(() => {
                                                            const months = parseInt(tile.payment_status.replace('instalment_', '')) || 0;
                                                            if (months && tile.created_at) {
                                                                const startDate = new Date(tile.created_at);
                                                                const endDate = new Date(startDate);
                                                                endDate.setMonth(endDate.getMonth() + months);
                                                                return formatDate(endDate.toISOString());
                                                            }
                                                            return 'N/A';
                                                        })()}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="payment-option full">Full</span>
                                            )}
                                        </td>
                                        <td>
                                            {tile.admin_notes ? (
                                                <span className="admin-notes" title={tile.admin_notes}>
                                                    {tile.admin_notes.length > 30
                                                        ? tile.admin_notes.substring(0, 30) + '...'
                                                        : tile.admin_notes}
                                                </span>
                                            ) : (
                                                <span style={{ color: '#999', fontSize: '12px' }}>-</span>
                                            )}
                                        </td>
                                        <td>{formatDate(tile.created_at)}</td>
                                        <td>
                                            {tile.status === 'pending' && (
                                                <>
                                                    <button
                                                        onClick={() => openVerifyModal(tile)}
                                                        className="approve-btn"
                                                        title="Verify and confirm this tile reservation"
                                                    >
                                                        Verify & Confirm
                                                    </button>
                                                    <button
                                                        onClick={() => openRejectModal(tile, 'reject')}
                                                        className="reject-btn"
                                                        title="Reject this tile reservation"
                                                    >
                                                        Reject
                                                    </button>
                                                    <button
                                                        onClick={() => openRejectModal(tile, 'delete')}
                                                        className="delete-btn"
                                                        title="Delete this tile reservation"
                                                    >
                                                        Delete
                                                    </button>
                                                </>
                                            )}
                                            {tile.status === 'confirmed' && (
                                                <span style={{ color: '#28a745', fontWeight: '500', fontSize: '13px' }}>
                                                    Locked
                                                </span>
                                            )}
                                            {tile.status === 'rejected' && (
                                                <button
                                                    onClick={() => openRejectModal(tile, 'delete')}
                                                    className="delete-btn"
                                                    title="Delete this rejected tile"
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {tileReservations.length === 0 && (
                                    <tr>
                                        <td colSpan="11" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                            No tile reservations yet
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'audit' && (
                    <div className="admin-section">
                        <div className="section-header">
                            <h2>Audit Trail</h2>
                            <button className="add-btn" onClick={fetchData}>Refresh</button>
                        </div>
                        <table className="admin-table audit-table">
                            <thead>
                                <tr>
                                    <th>Date/Time</th>
                                    <th>User</th>
                                    <th>Action</th>
                                    <th>Type</th>
                                    <th>Details</th>
                                    <th>IP Address</th>
                                    <th>Location</th>
                                </tr>
                            </thead>
                            <tbody>
                                {auditLogs.map(log => {
                                    const actionStyle = formatActionType(log.action_type);
                                    return (
                                        <tr key={log.id} className={log.action_type === 'login_failed' ? 'pending-row' : ''}>
                                            <td className="audit-time">
                                                {formatDateTime(log.created_at)}
                                            </td>
                                            <td>
                                                <strong>{log.username}</strong>
                                            </td>
                                            <td>{log.action}</td>
                                            <td>
                                                <span
                                                    className="action-type-badge"
                                                    style={{ backgroundColor: actionStyle.color }}
                                                >
                                                    {actionStyle.label}
                                                </span>
                                            </td>
                                            <td className="audit-details">{log.details || '-'}</td>
                                            <td className="audit-ip">{log.ip_address}</td>
                                            <td className="audit-location">{log.location}</td>
                                        </tr>
                                    );
                                })}
                                {auditLogs.length === 0 && (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                            No audit logs yet
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'users' && (
                    <div className="admin-section">
                        <div className="section-header">
                            <h2>Admin Users</h2>
                            <div className="tile-stats">
                                <span className="tile-stat">
                                    <strong>{adminUsers.filter(u => u.approval_status === 'pending').length}</strong> Pending
                                </span>
                                <span className="tile-stat">
                                    <strong>{adminUsers.filter(u => u.approval_status === 'approved').length}</strong> Approved
                                </span>
                                <span className="tile-stat">
                                    <strong>{adminUsers.filter(u => u.approval_status === 'rejected').length}</strong> Rejected
                                </span>
                            </div>
                        </div>

                        <div className="inquiries-info-banner">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <path d="M12 16v-4M12 8h.01"/>
                            </svg>
                            <p>Manage admin portal access. Only @pcic.org.au users can sign in. New users require approval before accessing the dashboard.
                            {currentUserIsPrimary && <strong> You are the Primary Admin - you can transfer this role and delete users.</strong>}</p>
                        </div>

                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Email</th>
                                    <th>Status</th>
                                    <th>Approved By</th>
                                    <th>Last Login</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {adminUsers.map(adminUser => (
                                    <tr key={adminUser.user_id} className={adminUser.approval_status === 'pending' ? 'pending-row' : ''}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                {adminUser.picture && (
                                                    <img
                                                        src={adminUser.picture}
                                                        alt=""
                                                        style={{ width: '32px', height: '32px', borderRadius: '50%' }}
                                                    />
                                                )}
                                                <div>
                                                    <strong>{adminUser.name || 'Unknown'}</strong>
                                                    {adminUser.is_primary_admin === 1 && (
                                                        <span style={{
                                                            marginLeft: '8px',
                                                            padding: '2px 8px',
                                                            background: '#ffc107',
                                                            color: '#000',
                                                            borderRadius: '12px',
                                                            fontSize: '11px',
                                                            fontWeight: 'bold'
                                                        }}>PRIMARY</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>{adminUser.email}</td>
                                        <td>
                                            <span className={`status-badge status-${adminUser.approval_status === 'approved' ? 'approved' : adminUser.approval_status === 'rejected' ? 'rejected' : 'pending'}`}>
                                                {adminUser.approval_status === 'approved' ? 'Approved' :
                                                 adminUser.approval_status === 'rejected' ? 'Rejected' : 'Pending'}
                                            </span>
                                        </td>
                                        <td>
                                            {adminUser.approved_by || '-'}
                                            {adminUser.approved_at && (
                                                <><br/><small>{formatDate(adminUser.approved_at)}</small></>
                                            )}
                                        </td>
                                        <td>
                                            {adminUser.last_login ? formatDateTime(adminUser.last_login) : 'Never'}
                                        </td>
                                        <td>
                                            {adminUser.approval_status === 'pending' && (
                                                <>
                                                    <button
                                                        onClick={() => updateUserApproval(adminUser.user_id, 'approve')}
                                                        className="approve-btn"
                                                    >
                                                        Approve
                                                    </button>
                                                    <button
                                                        onClick={() => updateUserApproval(adminUser.user_id, 'reject')}
                                                        className="reject-btn"
                                                    >
                                                        Reject
                                                    </button>
                                                </>
                                            )}
                                            {adminUser.approval_status === 'approved' && adminUser.email !== user?.email && (
                                                <>
                                                    <button
                                                        onClick={() => updateUserApproval(adminUser.user_id, 'reject')}
                                                        className="reject-btn"
                                                    >
                                                        Revoke
                                                    </button>
                                                    {currentUserIsPrimary && !adminUser.is_primary_admin && (
                                                        <>
                                                            <button
                                                                onClick={() => transferPrimaryAdmin(adminUser.user_id, adminUser.email)}
                                                                className="approve-btn"
                                                                style={{ marginLeft: '5px', background: '#ffc107', color: '#000' }}
                                                            >
                                                                Make Primary
                                                            </button>
                                                            <button
                                                                onClick={() => deleteUser(adminUser.user_id, adminUser.email)}
                                                                className="reject-btn"
                                                                style={{ marginLeft: '5px', background: '#dc3545' }}
                                                            >
                                                                Delete
                                                            </button>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                            {adminUser.approval_status === 'rejected' && (
                                                <>
                                                    <button
                                                        onClick={() => updateUserApproval(adminUser.user_id, 'approve')}
                                                        className="approve-btn"
                                                    >
                                                        Approve
                                                    </button>
                                                    {currentUserIsPrimary && (
                                                        <button
                                                            onClick={() => deleteUser(adminUser.user_id, adminUser.email)}
                                                            className="reject-btn"
                                                            style={{ marginLeft: '5px', background: '#dc3545' }}
                                                        >
                                                            Delete
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {adminUsers.length === 0 && (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                            No users found. Users will appear here after signing in with their @pcic.org.au Google account.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>

            {/* Verification Modal */}
            {showVerifyModal && verifyingTile && (
                <div className="modal-overlay" onClick={closeVerifyModal}>
                    <div className="modal-content verify-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Verify Tile #{verifyingTile.tile_number}</h3>
                            <button className="modal-close" onClick={closeVerifyModal}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <p style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
                                Please review and confirm the details below. Once confirmed, this tile will be <strong>locked</strong> and cannot be deleted or reverted.
                            </p>

                            <div className="form-group">
                                <label>Full Name</label>
                                <input
                                    type="text"
                                    value={verifyFullName}
                                    onChange={(e) => setVerifyFullName(e.target.value)}
                                    placeholder="Donor's full name"
                                />
                            </div>

                            <div className="form-group">
                                <label>Name to Display on Tile *</label>
                                <input
                                    type="text"
                                    value={verifyDisplayName}
                                    onChange={(e) => setVerifyDisplayName(e.target.value)}
                                    placeholder="Name shown on the tile"
                                    required
                                />
                                <small style={{ color: '#888', fontSize: '12px' }}>This is the name that will appear on the wall</small>
                            </div>

                            <div className="form-group">
                                <label>Email Address *</label>
                                <input
                                    type="email"
                                    value={verifyEmail}
                                    onChange={(e) => setVerifyEmail(e.target.value)}
                                    placeholder="Donor's email"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Phone Number</label>
                                <input
                                    type="tel"
                                    value={verifyPhone}
                                    onChange={(e) => setVerifyPhone(e.target.value)}
                                    placeholder="Donor's phone"
                                />
                            </div>

                            <div style={{
                                background: '#fff3cd',
                                border: '1px solid #ffc107',
                                borderRadius: '6px',
                                padding: '12px',
                                marginTop: '20px',
                                fontSize: '13px',
                                color: '#856404'
                            }}>
                                <strong>Warning:</strong> This action cannot be undone. Once confirmed, the tile will be permanently locked with these details displayed on the Wall of Akhirah.
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={closeVerifyModal}>Cancel</button>
                            <button className="btn-primary" onClick={confirmVerification}>
                                Confirm & Lock Tile
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Confirmation Modal */}
            {showPaymentModal && paymentTile && (
                <div className="modal-overlay" onClick={closePaymentModal}>
                    <div className="modal-content verify-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Confirm Payment - Tile #{paymentTile.tile_number}</h3>
                            <button className="modal-close" onClick={closePaymentModal}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <p style={{ marginBottom: '16px', color: '#666', fontSize: '14px' }}>
                                Confirm payment after verifying in Stripe Dashboard.
                            </p>

                            {/* Donor Details Box */}
                            <div style={{
                                background: '#d1ecf1',
                                border: '1px solid #bee5eb',
                                borderRadius: '6px',
                                padding: '12px',
                                marginBottom: '16px',
                                fontSize: '13px',
                                color: '#0c5460'
                            }}>
                                <strong>Tile & Donor Details:</strong>
                                <table style={{ width: '100%', marginTop: '8px', fontSize: '13px' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ padding: '4px 0', fontWeight: '500', width: '120px' }}>Tile Number:</td>
                                            <td style={{ padding: '4px 0' }}>#{paymentTile.tile_number}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ padding: '4px 0', fontWeight: '500' }}>Full Name:</td>
                                            <td style={{ padding: '4px 0' }}>{paymentTile.donor_full_name || paymentTile.donor_name || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ padding: '4px 0', fontWeight: '500' }}>Display Name:</td>
                                            <td style={{ padding: '4px 0' }}>{paymentTile.donor_name || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ padding: '4px 0', fontWeight: '500' }}>Email:</td>
                                            <td style={{ padding: '4px 0' }}>{paymentTile.donor_email || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ padding: '4px 0', fontWeight: '500' }}>Phone:</td>
                                            <td style={{ padding: '4px 0' }}>{paymentTile.donor_phone || 'N/A'}</td>
                                        </tr>
                                        {paymentTile.payment_method_type && (
                                            <tr>
                                                <td style={{ padding: '4px 0', fontWeight: '500' }}>Payment Method:</td>
                                                <td style={{ padding: '4px 0' }}>{formatPaymentMethod(paymentTile.payment_method_type, paymentTile.payment_method_brand)}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="form-group">
                                <label>Stripe Payment ID *</label>
                                <input
                                    type="text"
                                    value={stripePaymentId}
                                    onChange={(e) => setStripePaymentId(e.target.value)}
                                    placeholder="e.g., pi_3ABC123xyz..."
                                    required
                                />
                                <small style={{ color: '#888', fontSize: '12px' }}>
                                    Enter the Stripe Payment Intent ID (pi_...) or Checkout Session ID (cs_...)
                                </small>
                            </div>

                            <div className="form-group">
                                <label>Additional Notes (optional)</label>
                                <textarea
                                    value={paymentNotes}
                                    onChange={(e) => setPaymentNotes(e.target.value)}
                                    placeholder="Any additional notes about this payment confirmation..."
                                    rows={3}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        resize: 'vertical'
                                    }}
                                />
                                <small style={{ color: '#888', fontSize: '12px' }}>
                                    Payment details (date/time, Stripe ID, method) will be automatically recorded.
                                </small>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={closePaymentModal}>Cancel</button>
                            <button className="btn-primary" onClick={confirmPayment}>
                                Confirm Payment
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rejection/Delete Modal */}
            {showRejectModal && rejectingTile && (
                <div className="modal-overlay" onClick={closeRejectModal}>
                    <div className="modal-content verify-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{rejectAction === 'delete' ? 'Delete' : 'Reject'} Tile #{rejectingTile.tile_number}</h3>
                            <button className="modal-close" onClick={closeRejectModal}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <div style={{
                                background: '#f8d7da',
                                border: '1px solid #f5c6cb',
                                borderRadius: '6px',
                                padding: '12px',
                                marginBottom: '20px',
                                fontSize: '13px',
                                color: '#721c24'
                            }}>
                                <strong>Donor Details:</strong><br/>
                                Name: {rejectingTile.donor_full_name || rejectingTile.donor_name}<br/>
                                Email: {rejectingTile.donor_email}<br/>
                                Phone: {rejectingTile.donor_phone || 'N/A'}
                            </div>

                            <div className="form-group">
                                <label>Reason for {rejectAction === 'delete' ? 'Deletion' : 'Rejection'} * (minimum 5 words)</label>
                                <textarea
                                    value={rejectNotes}
                                    onChange={(e) => setRejectNotes(e.target.value)}
                                    placeholder={`Please explain why this tile is being ${rejectAction === 'delete' ? 'deleted' : 'rejected'}...`}
                                    rows={4}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        resize: 'vertical'
                                    }}
                                    required
                                />
                                <small style={{ color: '#888', fontSize: '12px' }}>
                                    Word count: {rejectNotes.trim().split(/\s+/).filter(w => w.length > 0).length} / 5 minimum
                                </small>
                            </div>

                            <div style={{
                                background: '#fff3cd',
                                border: '1px solid #ffc107',
                                borderRadius: '6px',
                                padding: '12px',
                                marginTop: '16px',
                                fontSize: '13px',
                                color: '#856404'
                            }}>
                                <strong>Warning:</strong> {rejectAction === 'delete'
                                    ? 'This will permanently delete the tile reservation. This action cannot be undone.'
                                    : 'This will mark the tile as rejected. The tile will be released for others to claim.'}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={closeRejectModal}>Cancel</button>
                            <button
                                className="btn-primary"
                                onClick={confirmRejectAction}
                                style={{ background: '#dc3545', borderColor: '#dc3545' }}
                            >
                                {rejectAction === 'delete' ? 'Delete Tile' : 'Reject Tile'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
